import React from 'react';
import { playButtonTap } from '../../utils/audioEngine';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  LinearProgress,
  Button,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useGame } from '../../contexts/GameContext';
import { CURATED_JOURNEY_MAP } from '../../utils/curatedJourneys';

const LevelGrid = ({ onSelectLevel, onChangeJourney }) => {
  const {
    isLevelUnlocked,
    getLevelStars,
    getHighScore,
    journeyPreferences,
    journeyPersona,
    journeySequence,
    curatedJourneyId,
  } = useGame();

  const curatedInfo = curatedJourneyId ? CURATED_JOURNEY_MAP[curatedJourneyId] : null;

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy':   return '#4caf50';
      case 'medium': return '#ff9800';
      case 'hard':   return '#f44336';
      case 'expert': return '#9c27b0';
      default:       return '#757575';
    }
  };

  const renderStars = (levelId) => {
    const stars = getLevelStars(levelId);
    return (
      <Box display="flex" justifyContent="center" mt={1}>
        {[1, 2, 3, 4, 5].map((star) =>
          star <= stars ? (
            <StarIcon key={star} sx={{ color: '#ffd700', fontSize: 20 }} />
          ) : (
            <StarBorderIcon key={star} sx={{ color: '#ccc', fontSize: 20 }} />
          )
        )}
      </Box>
    );
  };

  // ── No journey set yet ──────────────────────────────────────────────────
  if (!journeySequence || journeySequence.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Box sx={{ fontSize: '4rem', mb: 2 }}>🎵</Box>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
          Build Your Personal Journey
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Tell us your goals and style so we can craft a unique 50-lesson path just for you.
        </Typography>
        <Button
          variant="contained"
          size="large"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => onChangeJourney?.()}
          sx={{ backgroundColor: '#58CC02', borderRadius: 3, px: 4, py: 1.5, fontWeight: 'bold' }}
        >
          Start My Journey
        </Button>
      </Container>
    );
  }

  // ── Journey loaded — show 50-level grid ─────────────────────────────────
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* ── Curated Journey Banner ─────────────────────────────────────── */}
      {curatedInfo && (
        <Box
          sx={{
            mb: 3,
            p: 2.5,
            borderRadius: 3,
            background: curatedInfo.gradient,
            boxShadow: `0 4px 24px ${curatedInfo.glowColor}`,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Box sx={{ fontSize: '2.4rem', lineHeight: 1, flexShrink: 0, mt: 0.2 }}>{curatedInfo.emoji}</Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#fff', lineHeight: 1.2, letterSpacing: 0.5 }}>
              {curatedInfo.name} Path
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.4, mb: 1.5 }}>
              {curatedInfo.tagline}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => onChangeJourney?.()}
              sx={{
                borderColor: 'rgba(255,255,255,0.7)',
                color: '#fff',
                borderRadius: 2,
                fontWeight: 'bold',
                fontSize: '0.72rem',
                '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.15)' },
              }}
            >
              Change Journey
            </Button>
          </Box>
        </Box>
      )}

      {/* ── Tag-based persona banner ────────────────────────────────────── */}
      {!curatedInfo && journeyPersona && journeyPreferences.length > 0 && (
        <Box
          sx={{
            mb: 3,
            p: 2.5,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${journeyPersona.color}22 0%, ${journeyPersona.color}44 100%)`,
            border: `1.5px solid ${journeyPersona.color}55`,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Box sx={{ fontSize: '2.2rem', lineHeight: 1, flexShrink: 0, mt: 0.3 }}>{journeyPersona.icon}</Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: journeyPersona.color, lineHeight: 1.2 }}>
              {journeyPersona.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4, mb: 1.5 }}>
              {journeyPersona.desc}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => onChangeJourney?.()}
              sx={{
                borderColor: journeyPersona.color,
                color: journeyPersona.color,
                borderRadius: 2,
                fontWeight: 'bold',
                fontSize: '0.72rem',
              }}
            >
              Change Journey
            </Button>
          </Box>
        </Box>
      )}

      <Box textAlign="center" mb={4}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#58CC02' }}>
          {curatedInfo
            ? `${curatedInfo.emoji} ${curatedInfo.name} Journey (${journeySequence.length} Lessons)`
            : `🎮 Your ${journeySequence.length}-Lesson Journey`}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {curatedInfo
            ? curatedInfo.description
            : 'Complete each lesson with 3+ stars to unlock the next step in your path!'}
        </Typography>
      </Box>

      <Grid container spacing={3} alignItems="stretch">
        {journeySequence.map((level, index) => {
          const step = index + 1;
          const unlocked = isLevelUnlocked(level.id);
          const stars = getLevelStars(level.id);
          const highScore = getHighScore(level.id);
          const completed = stars > 0;

          return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`${level.id}-${index}`}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  opacity: unlocked ? 1 : 0.6,
                  transition: 'all 0.3s',
                  minHeight: 380,
                  '&:hover': unlocked
                    ? { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }
                    : {},
                }}
              >
                <CardActionArea
                  onClick={() => { if (unlocked) { playButtonTap(); onSelectLevel(level); } }}
                  disabled={!unlocked}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    justifyContent: 'flex-start',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {/* Lock overlay */}
                    {!unlocked && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: 2,
                        }}
                      >
                        <LockIcon sx={{ fontSize: 64, color: '#999' }} />
                      </Box>
                    )}

                    {/* Step number badge */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        backgroundColor: getDifficultyColor(level.difficulty),
                        color: 'white',
                        borderRadius: '50%',
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: step < 10 ? '1.2rem' : '1rem',
                      }}
                    >
                      {step}
                    </Box>

                    {/* Content */}
                    <Box sx={{ pt: 2, filter: unlocked ? 'none' : 'blur(2px)', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', minHeight: 32 }}>
                        {level.name}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 48, lineHeight: 1.5 }}>
                        {level.description}
                      </Typography>

                      {/* Note Display */}
                      <Box
                        sx={{
                          backgroundColor: '#f5f5f5',
                          borderRadius: 2,
                          p: 1,
                          mb: 2,
                          textAlign: 'center',
                          minHeight: 64,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#58CC02', fontSize: '1.1rem' }}>
                          {level.notes.map((n) => n.display).join(' - ')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {level.notes.length} note{level.notes.length > 1 ? 's' : ''} •{' '}
                          {level.notes.reduce((sum, n) => sum + n.duration, 0).toFixed(1)}s total
                        </Typography>
                      </Box>

                      {/* Difficulty Chip */}
                      <Box display="flex" justifyContent="center" mb={1}>
                        <Chip
                          label={level.difficulty.toUpperCase()}
                          size="small"
                          sx={{ backgroundColor: getDifficultyColor(level.difficulty), color: 'white', fontWeight: 'bold' }}
                        />
                      </Box>

                      {/* Stars */}
                      <Box sx={{ minHeight: 32, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {completed && renderStars(level.id)}
                      </Box>

                      {/* High Score */}
                      <Box sx={{ mt: 1, minHeight: 40 }}>
                        {highScore > 0 && (
                          <>
                            <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                              Best: {highScore}%
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={highScore}
                              sx={{
                                mt: 0.5,
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: '#e0e0e0',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor:
                                    highScore >= 90 ? '#4caf50' : highScore >= 70 ? '#ff9800' : '#f44336',
                                },
                              }}
                            />
                          </>
                        )}
                      </Box>

                      <Box sx={{ flexGrow: 1 }} />

                      {/* Play / Locked CTA */}
                      {unlocked ? (
                        <Box sx={{ mt: 2, px: 0.5 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            size="medium"
                            sx={{
                              '&.MuiButton-root': {
                                background: completed
                                  ? 'linear-gradient(90deg, #ff8c00 0%, #e65c00 100%) !important'
                                  : 'linear-gradient(90deg, #58CC02 0%, #3da801 100%) !important',
                                color: '#fff',
                                fontWeight: 800,
                                fontSize: '0.95rem',
                                borderRadius: 2.5,
                                py: 1.2,
                                textTransform: 'none',
                                boxShadow: completed
                                  ? '0 4px 14px rgba(255,140,0,0.45)'
                                  : '0 4px 14px rgba(88,204,2,0.45)',
                                letterSpacing: 0.4,
                              },
                              '&.MuiButton-root:hover': {
                                background: completed
                                  ? 'linear-gradient(90deg, #e65c00 0%, #bf4c00 100%) !important'
                                  : 'linear-gradient(90deg, #46a302 0%, #3a8c01 100%) !important',
                              },
                            }}
                          >
                            {completed ? '🔄 Play Again' : '▶ Start Lesson'}
                          </Button>
                        </Box>
                      ) : (
                        <Box sx={{ mt: 2, textAlign: 'center', minHeight: 36 }}>
                          <Typography variant="caption" color="text.secondary">
                            Earn 3★ on Step {step - 1} to unlock
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Info Box */}
      <Box sx={{ mt: 4, p: 3, backgroundColor: '#e8f5e9', borderRadius: 2, border: '1px solid #a5d6a7' }}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          💡 <strong>Tip:</strong> Your 50-lesson journey is personalized to your goals and style.
          Earn 3 stars to advance to the next step. Want a different path?{' '}
          <Box
            component="span"
            onClick={() => onChangeJourney?.()}
            sx={{ color: '#58CC02', fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Change your journey
          </Box>
          {' '}anytime! 🎵
        </Typography>
      </Box>
    </Container>
  );
};

export default LevelGrid;
